import Input from "Components/Form/Input";
import { Field, Form, useFormikContext } from "formik";
import { usePreOrderDetails } from "Pages/PreBudgetConvertion/hooks/usePreOrderDetails";
import { useParams } from "react-router";
import { Edit, Check } from "lucide-react";
import { Box } from "styles/globalComponents";
import { Button } from "Components/Button";
import { InputPhone } from "Components/Form/PhoneInput";
import { MaskCPF } from "Utils/Masks";
import { Values } from "Pages/PreBudgetConvertionUnit/hooks/useCartDetails";
import { useItemsContext } from "Pages/PreBudgetConvertionUnit/hooks/useItemsContext";
import { useQuery } from "react-query";
import { getUnitsUser } from "Requests/HomeOrder";
import { useEffect, useState } from "react";

type InfoDetailsProps = {
  editFields: boolean;
  setEditFields: (edit: boolean) => void;
};

export const InfoDetails = ({
  editFields,
  setEditFields,
}: InfoDetailsProps) => {
  const {
    values,
    handleChange,
    isSubmitting,
    setFieldValue,
    setFieldTouched,
    touched,
    errors,
    validateField,
  } = useFormikContext<Values>();

  const [unitSelected, setUnitSelected] = useState("");

  const { id } = useParams<{ id: string }>();

  const { blockUserInteraction } = useItemsContext();

  const preOderDetailsAPI = usePreOrderDetails(Number(id));

  let dateSchedule = "Não informado";
  let timeSchedule = "Não informado";
  let zipcode = "";

  const { client, address, appointment_at, gender, unit_id } =
    preOderDetailsAPI.data || {};

  const units = useQuery({
    queryFn: getUnitsUser,
    queryKey: "user-units",
    refetchOnWindowFocus: false,
  });

  const getGenders = (patientGender?: string) => {
    switch (patientGender?.toLowerCase()) {
      case "m":
        return "Masculino";
      case "f":
        return "Feminino";
      default:
        return "Indefinido";
    }
  };

  if (appointment_at) {
    const [date, time] = appointment_at?.split(" ") || [];
    const [yyyy, mm, dd] = date?.split("-");
    timeSchedule = `${time?.slice(0, 5)} hrs`;
    dateSchedule = `${dd}/${mm}/${yyyy}`;
  }

  if (address?.zipcode) {
    const zip = address?.zipcode.replaceAll("-", "");
    zipcode = `${zip.slice(0, 5)}-${zip.slice(5, 8)}`;
  }

  const preOrderAddressDetails = [
    { name: "Rua", value: [address?.street || ""] },
    {
      name: "Número",
      value: [address?.number || ""],
    },
    { name: "Bairro", value: [address?.neighborhood || ""] },
    { name: "Complemento", value: [address?.complement || ""] },
    { name: "CEP", value: [zipcode] },
    { name: "Cidade", value: [address?.city || ""] },
    { name: "Estado", value: [address?.state || ""] },
  ];

  const onBlurPhone = () => {
    if (!touched.phone) {
      setFieldTouched("phone", true);
    }
    validateField("phone");
  };

  useEffect(() => {
    if (unit_id && units.data?.length >= 1) {
      const unitFound = units.data.find(
        (unit: any) => unit.id === Number(unit_id)
      );

      if (unitFound) {
        setUnitSelected(unitFound.name);
      }
    }
  }, [units.data]);

  return (
    <Form>
      <div className="tab-item patient-info">
        <h5 className="text-center">Dados de agendamento</h5>

        <div className="patient">
          <Box display="flex" justify="space-between" align="center">
            <h3>Dados do paciente</h3>

            {editFields ? (
              <Button
                classButton="edit-btn"
                onClick={() => setEditFields(false)}
                loading={isSubmitting}
                disabled={isSubmitting || blockUserInteraction}
              >
                <Check size={15} />
              </Button>
            ) : (
              <Button
                classButton="edit-btn"
                onClick={() => setEditFields(!editFields)}
                disabled={blockUserInteraction}
              >
                <Edit size={15} />
              </Button>
            )}
          </Box>

          <div className="item">
            <span>
              <strong>Nome:</strong>{" "}
            </span>

            {editFields ? (
              <Field
                id="name"
                type="text"
                onChange={handleChange}
                value={values.name}
                component={Input}
                className="input _opacity-gray"
                error={touched.name && errors.name}
              />
            ) : (
              <span>{values.name}</span>
            )}
          </div>

          <div className="item">
            <span>
              <strong>Documento:</strong>{" "}
            </span>

            {editFields ? (
              <Field
                id="document"
                type="text"
                value={values.document}
                onChange={handleChange}
                component={Input}
                className="input _opacity-gray"
                error={touched.document && errors.document && errors.document}
              />
            ) : (
              <span>{MaskCPF(values.document)}</span>
            )}
          </div>

          <div className="item">
            <span>
              <strong>Telefone: </strong>
            </span>

            {editFields ? (
              <Field
                id="phone"
                type="text"
                value={values.phone}
                onChange={(phone: string) => setFieldValue("phone", phone)}
                onBlur={onBlurPhone}
                component={InputPhone}
                className="input _opacity-gray"
                error={touched.phone && errors.phone}
              />
            ) : (
              <span>{values.phone}</span>
            )}
          </div>

          <div className="item">
            <span>
              <strong>Convênio: </strong>
            </span>
            <span>{client?.name}</span>
          </div>

          <div className="item">
            <span>
              <strong>Médico: </strong>
            </span>

            {editFields ? (
              <Field
                id="doctor"
                type="text"
                value={values.doctor}
                onChange={handleChange}
                component={Input}
                className="input _opacity-gray"
                error={touched.doctor && errors.doctor}
              />
            ) : (
              <span>{values.doctor}</span>
            )}
          </div>

          <div className="item">
            <span>
              <strong>CRM: </strong>
            </span>

            {editFields ? (
              <Field
                id="crm"
                type="text"
                value={values.crm}
                onChange={handleChange}
                component={Input}
                className="input _opacity-gray"
                error={touched.crm && errors.crm}
              />
            ) : (
              <span>{values.crm}</span>
            )}
          </div>
          <div className="item">
            <span>
              <strong>Nome da Mãe: </strong>
            </span>

            {editFields ? (
              <Field
                id="mothers_full_name"
                type="text"
                value={values.mothers_full_name}
                onChange={handleChange}
                component={Input}
                className="input _opacity-gray"
                error={touched.mothers_full_name && errors.mothers_full_name}
              />
            ) : (
              <span>{values.mothers_full_name}</span>
            )}
          </div>

          <div className="item">
            <span>
              <strong>Gênero biológico: </strong>
            </span>

            <span>{getGenders(gender)}</span>
          </div>
        </div>

        <div className="selected-unit">
          <h3>Unidade selecionada:</h3>

          <div className="item select-unit">
            <strong>
              <span>{unitSelected}</span>
            </strong>
          </div>
        </div>

        <div className="schedule">
          <h3>Dados do agendamento</h3>

          <div className="item">
            <span>
              <strong>Dia:</strong>{" "}
            </span>
            <span>{dateSchedule}</span>
          </div>

          <div className="item">
            <span>
              <strong>Horário:</strong>{" "}
            </span>
            <span>{timeSchedule}</span>
          </div>
        </div>

        {address?.zipcode && (
          <div className="address">
            <h3>Dados de endereço</h3>

            {preOrderAddressDetails.map((item) => (
              <div className="item">
                <span>
                  <strong>{item.name}:</strong>{" "}
                </span>
                <span>{item.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Form>
  );
};
